// Story 11.4 — the Story 1.15 transaction-history surface, with the receipt
// link on it.
//
// WalletPage already fetched `/wallet/history` (it derives the balance
// provenance breakdown from the first page) and then rendered a placeholder
// where the rows should be. This renders them, because the acceptance criterion
// is that a receipt is reachable "from their transaction history, not only by
// email" — and a link needs a row to sit on.
//
// ⚠ DELIBERATELY THE SMALL VERSION. No pagination, no filters, no date
// grouping: Story 1.15's full history list is still unbuilt and this does not
// claim to be it. It renders the page WalletPage already had in hand.
//
// Styling is entirely the existing system — `rounded-lg border bg-card`,
// `font-body`, `tabular-nums`, `text-muted-foreground`, and the shared
// <EmptyState>. Nothing new is invented here.

import { Link } from "react-router-dom";

import { EmptyState } from "../../components/EmptyState";

export type LedgerHistoryRow = {
  id: string;
  direction: "credit" | "debit";
  amount_cents: number;
  provenance: string;
  created_at: string;
  /** Story 11.4 — present on rows that have a document. Absent, not null. */
  receipt_id?: string;
  /** The formatted series number, e.g. `GH-000001`. */
  receipt_number?: string;
  /**
   * Story 11.7 / OQ-1 — `receipt` | `credit_note` | `debit_note`. Present
   * exactly when `receipt_id` is.
   *
   * ⚑ THE SERIES IS SHARED, SO THE LABEL HAS TO SAY WHICH DOCUMENT IT IS.
   * Without this, a refund's credit note and a top-up's receipt are two rows in
   * the same list that look identical and say opposite things about where the
   * money went.
   */
  receipt_document_type?: string;
};

/// The link's word, per document type. A document type this build does not know
/// still gets a link — a document the user cannot open is worse than one whose
/// label is merely generic.
const DOCUMENT_LINK_LABELS: Record<string, string> = {
  receipt: "Receipt",
  credit_note: "Credit note",
  debit_note: "Debit note",
};

function documentLabel(documentType: string | undefined): string {
  return (documentType && DOCUMENT_LINK_LABELS[documentType]) || "Document";
}

function formatEur(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const euros = Math.floor(abs / 100);
  const remainder = abs % 100;
  return `${sign}€${euros}.${remainder.toString().padStart(2, "0")}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/// The user's word for each movement. `provenance` is the ledger's vocabulary,
/// not a person's — a wallet page says "Top-up", never `top_up`.
const LABELS: Record<string, string> = {
  top_up: "Top-up",
  refund_to_source: "Refund to your card",
  primary_dossier_purchase: "Dossier purchase",
  marketplace_sale: "Marketplace sale",
  marketplace_purchase: "Marketplace purchase",
  settlement_reserved: "Held for a purchase",
  settlement_captured: "Purchase settled",
  escrow_held: "Held in escrow",
  escrow_released: "Released from escrow",
  offer_hold: "Held for an offer",
  offer_released: "Offer hold released",
  creator_primary_share: "Creator share",
  creator_royalty: "Creator royalty",
  platform_fee: "Platform fee",
  manual_adjustment: "Manual adjustment",
};

function label(provenance: string): string {
  return (
    LABELS[provenance] ??
    // An unknown provenance is shown readably rather than hidden: a row the
    // user cannot see is worse than one whose label is merely unpolished.
    provenance.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())
  );
}

export function RecentActivityList({ rows }: { rows: LedgerHistoryRow[] }) {
  if (rows.length === 0) {
    return (
      <EmptyState
        aria-label="No wallet activity yet"
        description="Nothing has moved through this wallet yet. Your top-ups, purchases and sales will appear here, each with its receipt."
      />
    );
  }

  return (
    <ul className="flex flex-col gap-3">
      {rows.map((row) => (
        <li
          key={row.id}
          className="flex flex-col gap-2 rounded-lg border bg-card p-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex flex-col gap-1">
            <div className="font-body text-sm font-semibold">
              {label(row.provenance)}
            </div>
            <div className="font-body text-xs text-muted-foreground">
              {formatDate(row.created_at)}
              {row.receipt_number && (
                <>
                  {" · "}
                  <span className="font-mono">{row.receipt_number}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-mono text-sm font-semibold tabular-nums">
              {row.direction === "credit" ? "+" : "-"}
              {formatEur(row.amount_cents)}
            </span>
            {row.receipt_id && (
              <Link
                to={`/account/wallet/receipts/${row.receipt_id}`}
                data-testid="wallet-receipt-link"
                className="font-body text-sm underline decoration-dotted"
              >
                {documentLabel(row.receipt_document_type)}
              </Link>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
